import type { Prompt } from '../types';

/** Result of a single exclusive save attempt. */
export type SaveAttemptResult =
  | { outcome: 'success'; savedAt: Date }
  | { outcome: 'error'; message: string }
  | { outcome: 'skipped' };

/** Mutable gate used to keep only one save write in flight. */
export type SaveGate = { busy: boolean };

export const DEFAULT_SAVE_ERROR = 'Failed to save prompt. Please try again.';

/**
 * Runs persist() under an exclusive gate so concurrent callers skip instead of
 * issuing duplicate writes. Failures are returned as { outcome: 'error' } and
 * never leave the gate stuck busy.
 */
export async function attemptSave(
  gate: SaveGate,
  persist: () => Promise<void>,
  now: () => Date = () => new Date()
): Promise<SaveAttemptResult> {
  if (gate.busy) {
    return { outcome: 'skipped' };
  }

  gate.busy = true;
  try {
    await persist();
    return { outcome: 'success', savedAt: now() };
  } catch (error) {
    const message =
      error instanceof Error && error.message.trim().length > 0
        ? error.message
        : DEFAULT_SAVE_ERROR;
    return { outcome: 'error', message };
  } finally {
    gate.busy = false;
  }
}

/**
 * Parent-facing save: awaits persistence so navigate/reset only happen after
 * success. Errors propagate so the form can keep its draft and show retry UI.
 */
export async function persistNewPrompt(
  save: (prompt: Prompt) => Promise<void>,
  prompt: Prompt
): Promise<void> {
  await save(prompt);
}

/** Test helper: a promise you can resolve/reject later. */
export function createDeferred<T = void>(): {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
} {
  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (reason?: unknown) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}
